import {Component, DestroyRef, EventEmitter, inject, Input, OnChanges, Output} from '@angular/core';
import {CreativeGalleryBase, GalleryNavigation} from '../types';
import {CreativeGalleryCardComponent} from './creative-gallery-card/creative-gallery-card.component';
import {NgFor, NgIf} from '@angular/common';
import {NoWidgetDataComponent} from '../no-widget-data/no-widget-data.component';
import {MatDialog} from '@angular/material/dialog';
import {CreativeCardModalComponent} from './creative-card-modal/creative-card-modal.component';
import {BrandsService} from '../../../services/brands.service';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {StateService} from '../../../services/state.service';
import {finalize} from 'rxjs';
import {DashboardService} from '../../../services/dashboard.service';
import {LoaderComponent} from '../../../shared/components/loader/loader.component';
import {PosthogService} from '../../../services/posthog.service';
import {PosthogEventName, PosthogEventCode} from '../../../services/types';
import {SocialCreativeGalleryCardComponent} from '../../social-overview-screen-a/creative-gallery/creative-gallery-card/creative-gallery-card.component';
import {BrandInGallery, SocialCreativeCard} from '../../social-overview-screen-a/types';

@Component({
  selector: 'app-creative-gallery',
  imports: [CreativeGalleryCardComponent, SocialCreativeGalleryCardComponent, NoWidgetDataComponent, NgFor, NgIf, LoaderComponent],
  templateUrl: './creative-gallery.component.html',
  styleUrl: './creative-gallery.component.scss'
})
export class CreativeGalleryComponent implements OnChanges {
  @Input() data: CreativeGalleryBase[] = [];
  @Input() brandskeys!: string[];
  @Input() getterName!: 'getMyCreativeCard' | 'getCompetitorCreativeCard' | 'getMessagingCreativeCard';
  @Input() loading = false;
  @Input() isMessagingDrawer = false;
  @Input() cardType: 'my-brand' | 'social' = 'my-brand';
  @Input() socialData: BrandInGallery[] = [];
  @Input() brandIconMap: Record<string, string> = {};
  @Input() sortBy = 'impressions';
  @Output() requestPageChange = new EventEmitter<GalleryNavigation>();

  protected socialCards: SocialCreativeCard[] = [];
  public numberOfCardsInRow = 5;
  private readonly WIDGET_NAME = 'creative_gallery';

  private brandsService = inject(BrandsService);
  private stateService = inject(StateService);
  private dashboardService = inject(DashboardService);
  private posthogService = inject(PosthogService);
  private dialog = inject(MatDialog);
  private destroyRef = inject(DestroyRef);

  ngOnChanges(): void {
    const flat = this.socialData.flatMap(brand =>
      brand.creatives.map(creative => ({
        ...creative,
        brandKey: brand.key,
        brandName: brand.name,
        brandLogoPath: this.brandIconMap[brand.key],
      }))
    );
    this.socialCards = this.sortSocialCards(flat);
  }

  protected get isEmpty(): boolean {
    return this.cardType === 'social' ? this.socialCards.length === 0 : this.data.length === 0;
  }

  private sortSocialCards(cards: SocialCreativeCard[]): SocialCreativeCard[] {
    return [...cards].sort((a, b) => {
      switch (this.sortBy) {
        case 'impressions_asc': return a.impressions - b.impressions;
        case 'cpm_desc':        return b.cpm - a.cpm;
        case 'cpm_asc':         return a.cpm - b.cpm;
        default:                return b.impressions - a.impressions;
      }
    });
  }

  openCardModal(id: string, actionType: 'open' | 'navigate' = 'open'): void {
    this.stateService.loading = true;
    this.posthogService.capture(PosthogEventName.CREATIVE_CARD_VIEW, {
      [PosthogEventCode.CREATIVE_CARD_VIEW]: id,
      [PosthogEventCode.WIDGET_NAME]: this.WIDGET_NAME,
      [PosthogEventCode.ACTION_TYPE]: actionType
    });
    const payload = {
      brandsKeys: this.brandskeys,
      creativeId: id,
      ...(this.dashboardService.getDashboardFiltersPayload())
    }
    this.brandsService[this.getterName](payload)
    .pipe(
      takeUntilDestroyed(this.destroyRef),
      finalize(() => {
        this.stateService.loading = false;
      })
    ).subscribe(result => {
      const dialogRef = this.dialog.open(CreativeCardModalComponent, {
        data: result,
        panelClass: 'creative-card-modal',
        autoFocus: false
      });
      dialogRef.afterClosed().pipe(
        takeUntilDestroyed(this.destroyRef),
      ).subscribe(action => {
        if (action === GalleryNavigation.NEXT || action === GalleryNavigation.PREV) {
          const currentIndex = this.data.findIndex((c) => c.id === id);
          if (currentIndex === -1) return;
          const isNext = action === GalleryNavigation.NEXT;
          const isPrev = action === GalleryNavigation.PREV;
          const isAtLast = currentIndex === this.data.length - 1;
          const isAtFirst = currentIndex === 0;
          if ((isNext && isAtLast) || (isPrev && isAtFirst)) {
            this.requestPageChange.emit(action);
            return;
          }
          const delta = isNext ? 1 : -1;
          const nextIndex = currentIndex + delta;
          const nextId = this.data[nextIndex].id;
          setTimeout(() => this.openCardModal(nextId, 'navigate'), 0);
        } else {
          this.posthogService.capture(PosthogEventName.CREATIVE_CARD_CLOSED, {
            [PosthogEventCode.CREATIVE_CARD_VIEW]: id,
            [PosthogEventCode.WIDGET_NAME]: this.WIDGET_NAME
          });
        }
      });
      this.stateService.loading = false;
    });
  }


}
